import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Crown, CheckCircle, FileText, Download, Eye, TrendingUp, Shield, 
  Sparkles, Zap, Star, ArrowRight, Users, Building2, Award, 
  BarChart3, Lock, Unlock, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ResumeUpgradePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('codearena_token');
    setIsLoggedIn(!!token);
  }, []);

  const handleUpgrade = async () => {
    const token = localStorage.getItem('codearena_token');
    if (!token) {
      toast.error('Please login to upgrade your resume');
      navigate('/codearena/auth');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/codearena/me/resume/purchase-premium`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        await cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate upgrade');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'ATS-Optimized PDF Export',
      description: 'Download your resume in a format that passes Applicant Tracking Systems used by top companies.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Eye,
      title: 'Company View Analytics',
      description: 'See which companies have viewed your resume, when they viewed it, and how long they spent.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: TrendingUp,
      title: 'Resume Score & Insights',
      description: 'Get a detailed score based on 6 categories with actionable suggestions to improve.',
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: Shield,
      title: 'Verified Developer Badge',
      description: 'Stand out with a verified badge that shows recruiters your credentials are authentic.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Sparkles,
      title: 'AI-Powered Enhancements',
      description: 'Get AI suggestions for improving your summary, project descriptions, and skills presentation.',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: Zap,
      title: 'Priority Visibility',
      description: 'Your profile ranks higher in recruiter searches, increasing your chances of being discovered.',
      color: 'from-rose-500 to-red-500'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Premium Users' },
    { value: '85%', label: 'Higher Response Rate' },
    { value: '3x', label: 'More Profile Views' },
    { value: '500+', label: 'Companies Trust Us' }
  ];

  const comparisons = [
    { feature: 'Basic Resume Builder', free: true, premium: true },
    { feature: 'Public Profile Link', free: true, premium: true },
    { feature: 'ATS-Optimized PDF Download', free: false, premium: true },
    { feature: 'Company View Tracking', free: false, premium: true },
    { feature: 'Resume Score Analytics', free: false, premium: true },
    { feature: 'Verified Developer Badge', free: false, premium: true },
    { feature: 'AI Content Suggestions', free: false, premium: true },
    { feature: 'Priority in Searches', free: false, premium: true },
    { feature: 'Industry Benchmarks', free: false, premium: true },
    { feature: 'Skill Gap Analysis', free: false, premium: true },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <Crown className="text-amber-400" size={18} />
              <span className="text-amber-400 font-medium">Premium Resume</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Stand Out to
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent"> Recruiters</span>
            </h1>
            
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Upgrade your resume with professional features that help you get noticed by top companies and land your dream job.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:opacity-90 transition-all flex items-center gap-2 shadow-lg shadow-amber-500/30 disabled:opacity-50"
                data-testid="hero-upgrade-btn"
              >
                {loading ? 'Processing...' : (
                  <>
                    <Crown size={20} />
                    Upgrade Now - ₹99
                  </>
                )}
              </button>
              
              {!isLoggedIn && (
                <Link
                  to="/codearena/auth"
                  className="px-8 py-4 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all"
                >
                  Login to Get Started
                </Link>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Premium Features</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Everything you need to create a standout resume and track your job search progress
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="group p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-purple-500/30 transition-all"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <feature.icon className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Free vs Premium</h2>
          <p className="text-gray-400">See what you get with the premium upgrade</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 border-b border-white/10">
            <div className="text-gray-400 font-medium">Feature</div>
            <div className="text-center text-gray-400 font-medium">Free</div>
            <div className="text-center">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-full">
                Premium
              </span>
            </div>
          </div>
          
          {comparisons.map((item, idx) => (
            <div 
              key={idx}
              className={`grid grid-cols-3 gap-4 p-4 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
            >
              <div className="text-gray-300 text-sm">{item.feature}</div>
              <div className="text-center">
                {item.free ? (
                  <CheckCircle size={20} className="inline text-green-500" />
                ) : (
                  <Lock size={18} className="inline text-gray-600" />
                )}
              </div>
              <div className="text-center">
                <CheckCircle size={20} className="inline text-green-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="relative bg-gradient-to-r from-purple-500/20 to-amber-500/20 border border-purple-500/30 rounded-3xl p-8 md:p-12 text-center overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30">
              <Crown className="text-white" size={40} />
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Stand Out?</h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Join thousands of developers who have upgraded their resume and landed jobs at top companies.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:opacity-90 transition-all flex items-center gap-2 shadow-lg shadow-amber-500/30 disabled:opacity-50"
              >
                {loading ? 'Processing...' : (
                  <>
                    <Crown size={20} />
                    Upgrade Now - Only ₹99
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <CheckCircle size={16} className="text-green-500" />
                Secure Payment
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle size={16} className="text-green-500" />
                Instant Access
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle size={16} className="text-green-500" />
                Lifetime Validity
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-white font-medium">Have questions about Premium?</p>
            <p className="text-gray-500 text-sm">Contact us at support@evalentx.com</p>
          </div>
          <Link
            to="/codearena/dashboard"
            className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
          >
            Back to Dashboard <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResumeUpgradePage;
